import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';
import './FAQPage.css';

const faqs = [
    {
      category: "Getting Started",
      icon: "🚀",
      questions: [
        {
          q: "How do I create an account?",
          a: "Tap \"Get Started\" and enter your email, password, and name. Registration takes under 30 seconds — no credit card required."
        },
        {
          q: "What does Hangrily cost?",
          a: "Hangrily offers a generous free tier that covers solo sessions and basic features. For unlimited swipes, couple mode, group sessions, and analytics, we offer an affordable Premium plan. No ads ever, regardless of your plan."
        },
        {
          q: "Do I need to download anything?",
          a: "Hangrily works instantly in your web browser on any device — no downloads required. We also offer native mobile apps on iOS and Android for the best experience."
        },
        {
          q: "What devices are supported?",
          a: "Any device with a modern browser: iPhones, Android phones, iPads, tablets, laptops, and desktops. The interface adapts automatically to your screen."
        }
      ]
    },
    {
      category: "How It Works",
      icon: "⚡",
      questions: [
        {
          q: "How long does a session take?",
          a: "Most sessions wrap up in 2–5 minutes. Solo is fastest at about 1–2 minutes. Couple sessions average 2–3 minutes, and group sessions run 3–5 minutes depending on your group size."
        },
        {
          q: "How many restaurants will I swipe on?",
          a: "Sessions start with up to 15 restaurants tailored to your preferences. Through up to 3 rounds of swiping, we narrow it down to your perfect match. Clear winners can end a session early."
        },
        {
          q: "What do the swipe options mean?",
          a: "You have three choices for each restaurant: 👎 Pass (not interested — eliminated), 👍 Like (sounds good — moves forward), and ❤️ Love (top pick — gets priority scoring). Use Love sparingly for restaurants you're genuinely excited about."
        },
        {
          q: "Can I redo a session if I don't like the result?",
          a: "Absolutely. Start a new session anytime with different preferences — there's no cooldown or limit on sessions."
        },
        {
          q: "Where does restaurant data come from?",
          a: "We source real-time restaurant data from verified providers including ratings, photos, hours, and addresses. Information is refreshed regularly to stay accurate."
        }
      ]
    },
    {
      category: "Couple Mode",
      icon: "💑",
      questions: [
        {
          q: "How do I link my partner?",
          a: "Go to your Profile, tap \"Link Partner,\" and enter their Hangrily email address. They'll be connected instantly. Both of you need Hangrily accounts first."
        },
        {
          q: "Can I unlink my partner later?",
          a: "Yes — visit the Partner Link screen to unlink anytime. This only removes the couple connection; both accounts stay intact."
        },
        {
          q: "What are hunger nudges?",
          a: "A playful push notification you send your partner that says \"I'm hungry — let's pick somewhere!\" It's a fun, no-argument way to kick off a meal decision."
        },
        {
          q: "Do we both need to be online simultaneously?",
          a: "Nope. One partner starts the session and the other gets notified to join when they're ready. Results appear once both finish swiping."
        },
        {
          q: "What if we can't agree on anything?",
          a: "Our algorithm is built to find common ground. Even with picky eaters, we'll surface runner-up options. You can also restart with broader filters."
        }
      ]
    },
    {
      category: "Group Mode",
      icon: "👥",
      questions: [
        {
          q: "How many people can join a group?",
          a: "Up to 10 participants per session — perfect for friend dinners, family outings, or team lunches."
        },
        {
          q: "How do people join my session?",
          a: "You'll get a unique 6-character code (e.g. ABC123) when you create a group session. Share it via text, email, or any messaging app. Anyone with the code can join."
        },
        {
          q: "How is the winner decided in group mode?",
          a: "Every participant swipes independently. Restaurants need majority approval (50%+) to advance each round. The restaurant with the highest combined score wins."
        },
        {
          q: "What happens if someone is slow to swipe?",
          a: "The session waits for all participants before advancing rounds. You'll see real-time status showing who's still swiping."
        }
      ]
    },
    {
      category: "Cashback & Rewards",
      icon: "💰",
      questions: [
        {
          q: "How does Hangrily cashback work?",
          a: "Premium members earn a flat $1 cashback on every verified food order — dine-in, takeout, or delivery. After you eat, upload a photo of your receipt or a screenshot of your order confirmation, and once it's verified the $1 lands in your cashback balance."
        },
        {
          q: "How much cashback can I earn?",
          a: "$1 per order, up to 10 orders per month — so up to $10 in cashback every month. Since Premium is $9.99/month, hitting the max 10 orders a month nearly covers your subscription cost."
        },
        {
          q: "How do I prove my order to get cashback?",
          a: "Just upload a photo of your itemized receipt or a screenshot of your delivery/order confirmation (DoorDash, Uber Eats, Grubhub, etc.). Orders of $10 or more qualify. Most submissions are auto-approved instantly when your receipt and location check out."
        },
        {
          q: "How do I cash out my earnings?",
          a: "Redeem your balance anytime from the Rewards screen for Amazon gift cards, Visa prepaid cards, restaurant gift cards, or apply it as credit toward your subscription. Gift cards arrive by email, usually within 24 hours, with no fees."
        }
      ]
    },
    {
      category: "Privacy & Security",
      icon: "🔒",
      questions: [
        {
          q: "Is my data secure?",
          a: "Yes. All data is encrypted in transit (TLS) and at rest. We use industry-standard security practices, regular audits, and never sell your information to third parties."
        },
        {
          q: "Do you track my location?",
          a: "Only when you actively start a session, to find nearby restaurants. Location data is used in the moment and is not stored permanently."
        },
        {
          q: "Can I delete my account and data?",
          a: "Yes — go to Profile → Settings → Delete Account. This permanently removes all your data: history, preferences, partner links, and personal information."
        },
        {
          q: "Are there ads or sponsored restaurant listings?",
          a: "No. Restaurant results are based purely on your preferences and our matching algorithm. We never accept payment to promote specific restaurants."
        }
      ]
    },
    {
      category: "Troubleshooting",
      icon: "🛠️",
      questions: [
        {
          q: "No restaurants are showing up near me.",
          a: "Ensure location access is enabled in your browser settings. Try refreshing the page or widening your distance filter. If issues persist, try a different browser."
        },
        {
          q: "My partner can't join our session.",
          a: "Double-check they're logged in to their Hangrily account and that you've linked their correct email. Try creating a fresh session if the issue continues."
        },
        {
          q: "The app feels slow or unresponsive.",
          a: "Try refreshing the page and clearing your browser cache. A weak internet connection can also cause sluggish performance. If the problem persists, email us at support@hangrily.app."
        },
        {
          q: "I found a bug — how do I report it?",
          a: "We appreciate it! Email support@hangrily.app with a description of the issue, your device/browser info, and a screenshot if possible. We investigate every report."
        }
      ]
    }
];

export default function FAQPage() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = ['all', ...faqs.map(f => f.category)];

  const filteredFaqs = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return faqs
      .filter(cat => activeCategory === 'all' || cat.category === activeCategory)
      .map(cat => ({
        ...cat,
        questions: cat.questions.filter(
          faq => !q || faq.q.toLowerCase().includes(q) || faq.a.toLowerCase().includes(q)
        )
      }))
      .filter(cat => cat.questions.length > 0);
  }, [searchQuery, activeCategory]);

  const totalResults = filteredFaqs.reduce((sum, cat) => sum + cat.questions.length, 0);

  function toggleFAQ(index) {
    setOpenIndex(openIndex === index ? null : index);
  }

  return (
    <div className="public-page">
      <SEO
        title="Hangrily FAQ — How the Restaurant Decision App Works, Pricing & More"
        description="Questions about Hangrily? Learn how the swipe-based restaurant picker works for solo, couple & group modes. Free sessions, pricing, supported cities, how it compares to random wheels & Google Maps. All answers here."
        path="/faq"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'FAQ', item: 'https://hangrily.app/faq' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.flatMap(cat =>
              cat.questions.map(faq => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a
                }
              }))
            )
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Frequently Asked Questions</h1>
          <p className="hero-subtitle">
            Find answers to common questions about Hangrily.<br />
            Can't find what you need? <a href="mailto:support@hangrily.app" className="hero-link">Reach out to us</a>.
          </p>
        </section>

        <section className="faq-section">
          <div className="faq-controls">
            <div className="faq-search-wrap">
              <span className="search-icon-label">🔍</span>
              <input
                type="text"
                className="faq-search-input"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setOpenIndex(null); }}
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>
            <div className="faq-category-tabs">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`faq-tab ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                >
                  {cat === 'all' ? 'All Topics' : cat}
                </button>
              ))}
            </div>
          </div>

          {searchQuery && (
            <p className="faq-result-count">
              {totalResults} result{totalResults !== 1 ? 's' : ''} found
            </p>
          )}

          <div className="faq-categories-list">
            {filteredFaqs.map((category, catIndex) => (
              <div key={catIndex} className="faq-cat-block">
                <div className="faq-cat-header">
                  <span className="faq-cat-icon">{category.icon}</span>
                  <h2>{category.category}</h2>
                </div>
                <div className="faq-accordion">
                  {category.questions.map((faq, qIndex) => {
                    const globalIndex = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === globalIndex;

                    return (
                      <div key={qIndex} className={`faq-accordion-item ${isOpen ? 'open' : ''}`}>
                        <button
                          className="faq-accordion-trigger"
                          onClick={() => toggleFAQ(globalIndex)}
                          aria-expanded={isOpen}
                        >
                          <span className="faq-q-text">{faq.q}</span>
                          <span className={`faq-chevron ${isOpen ? 'rotated' : ''}`}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        </button>
                        <div className={`faq-accordion-panel ${isOpen ? 'expanded' : ''}`}>
                          <p>{faq.a}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <div className="faq-empty">
                <span className="faq-empty-icon">🤷</span>
                <h3>No matching questions</h3>
                <p>Try a different search term or browse all topics.</p>
                <button className="btn btn-secondary" onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}>
                  Show All Questions
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="faq-support-banner">
          <div className="support-banner-inner">
            <div className="support-banner-text">
              <h2>Still need help?</h2>
              <p>Our support team typically responds within 24 hours.</p>
            </div>
            <div className="support-banner-actions">
              <a href="mailto:support@hangrily.app" className="btn btn-primary">
                Email Support
              </a>
              <button className="btn btn-secondary" onClick={() => navigate('/contact')}>
                Contact Page
              </button>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Ready to End the Debate?</h2>
          <p>Join thousands of people making faster, fairer restaurant decisions.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Try It Free — No Account Needed
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
